import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Button,
  LinearProgress,
  List,
  ListItemButton,
  Divider,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Speed as ReadinessIcon,
  FactCheck as WorksheetIcon,
  Quiz as TestIcon,
  Article as ArticleIcon,
  FileDownloadOutlined as DownloadIcon,
  ShoppingBag as ShoppingBagIcon,
  CollectionsBookmark,
  EmojiEvents,
  ChevronRight,
  TrendingUp,
  Timer,
  CheckCircleOutline,
  School as SchoolIcon,
  Home as HomeIcon,
  MenuBook as ImproveIcon,
  BarChart as ProgressIcon,
  ArrowBack,
  InsertDriveFileOutlined,
  AutoAwesome,
  SupportAgent,
  Recommend as RecommendIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import api from "../../api";
import { toast } from "react-toastify";
import {
  AREA_BUCKETS,
  dayKey,
  pct,
  resourceProgress,
  subjectPalette,
  sumResourceCounts,
} from "./utils/progressFormat";
import PackDownloadsDialog from "./components/PackDownloadsDialog";
import useStudentPortalMode from "../../hooks/useStudentPortalMode";
import { formatTrialEnd } from "../../utils/mobileSubscriptionFormat";
import { friendlyPackName, splitPackName } from "../../utils/packWording";
import GettingStartedCard from "../../components/GettingStartedCard";

const GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const ACCENT = "#764ba2";
const CARD_SX = {
  borderRadius: { xs: "18px", sm: "24px" },
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid #f0f0f0",
};

const RECENT_ACTIVITY_LIMIT = 10;

const StatTile = ({ label, value, caption, color = "#111827" }) => (
  <Box sx={{ textAlign: "center", px: { xs: 0.5, sm: 1 }, py: { xs: 0.75, sm: 0.5 }, minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{ color: "#7f8c8d", fontWeight: 700, display: "block", fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1.15rem", sm: "1.5rem" },
        color,
        lineHeight: 1.2,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
    {caption ? (
      <Typography
        variant="caption"
        sx={{
          color: "#95a5a6",
          fontWeight: 600,
          display: "block",
          fontSize: { xs: "0.6rem", sm: "0.75rem" },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {caption}
      </Typography>
    ) : null}
  </Box>
);

const ProgressRow = ({ label, value, right, color, status, statusColor, compact = false }) => (
  <Box sx={{ mb: compact ? 1 : 1.5, minWidth: 0 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 0.5,
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#374151",
          fontSize: compact ? { xs: "0.65rem", sm: "0.75rem" } : undefined,
          minWidth: 0,
          flex: "1 1 auto",
        }}
        noWrap={compact}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.5, sm: 0.75 },
          flexShrink: 0,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          maxWidth: "100%",
        }}
      >
        {status ? (
          compact ? (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: statusColor || "#95a5a6",
                fontSize: { xs: "0.55rem", sm: "0.65rem" },
                lineHeight: 1.2,
              }}
            >
              {status}
            </Typography>
          ) : (
            <Chip
              size="small"
              label={status}
              sx={{
                height: { xs: 16, sm: 18 },
                fontSize: { xs: "0.55rem", sm: "0.6rem" },
                fontWeight: 800,
                bgcolor: `${statusColor || "#95a5a6"}18`,
                color: statusColor || "#95a5a6",
                "& .MuiChip-label": { px: { xs: 0.75, sm: 1 } },
              }}
            />
          )
        ) : null}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: color || "#111827",
            fontSize: compact ? { xs: "0.65rem", sm: "0.75rem" } : undefined,
          }}
        >
          {right}
        </Typography>
      </Box>
    </Box>
    <LinearProgress
      variant="determinate"
      value={pct(value)}
      sx={{
        height: compact ? { xs: 5, sm: 7 } : { xs: 6, sm: 8 },
        borderRadius: 5,
        bgcolor: "#f1f2f6",
        "& .MuiLinearProgress-bar": {
          borderRadius: 5,
          background: color ? `linear-gradient(90deg, ${color}, ${color}a0)` : GRADIENT,
        },
      }}
    />
  </Box>
);

const WorksheetRow = ({ worksheet, areaLabel, onOpen }) => (
  <ListItemButton
    onClick={onOpen}
    sx={{ borderRadius: "12px", border: "1px solid #f0f0f0", py: 1, px: 1.5, gap: 1 }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "#111827",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {worksheet.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "#95a5a6",
          fontWeight: 600,
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {areaLabel ||
          [worksheet.questions_count ? `${worksheet.questions_count} questions` : "", worksheet.difficulty]
            .filter(Boolean)
            .join(" · ")}
      </Typography>
    </Box>
    {worksheet.has_attempted ? (
      <Chip size="small" label="Done" sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#dcfce7", color: "#16a34a", flexShrink: 0 }} />
    ) : null}
    <ChevronRight sx={{ color: "#7f8c8d", flexShrink: 0 }} fontSize="small" />
  </ListItemButton>
);

const SectionHeading = ({ icon, children, action }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: { xs: 1.5, sm: 2 },
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        color: "#111827",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: { xs: "1.05rem", sm: "1.25rem" },
        minWidth: 0,
      }}
    >
      {icon}
      {children}
    </Typography>
    {action}
  </Box>
);

const MobileStudentDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const studentPrefix = process.env.REACT_APP_STUDENT_PREFIX || "student";
  // A free trial opens everything; say when it ends so the lock that follows is no surprise.
  const { subscriptionLock } = useStudentPortalMode();
  const onTrial = subscriptionLock?.isTrial === true;
  const trialEndsLabel = onTrial ? formatTrialEnd(subscriptionLock?.trialEndsAt) : "";

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gateMessage, setGateMessage] = useState(null);
  const [overview, setOverview] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [stats, setStats] = useState(null);
  const [completedToday, setCompletedToday] = useState(null);
  const [examBoards, setExamBoards] = useState([]);
  const [examBoardId, setExamBoardId] = useState(null);
  const [subjectCounts, setSubjectCounts] = useState({});
  const [purchases, setPurchases] = useState({ papers: [], others: [] });
  const [gameScore, setGameScore] = useState(null);

  const [drillSubject, setDrillSubject] = useState(null);
  const [drillBucket, setDrillBucket] = useState("weak_areas");
  const [areas, setAreas] = useState(null);
  const [areasLoading, setAreasLoading] = useState(false);

  const [actionDialog, setActionDialog] = useState(null);
  const [allWeakAreas, setAllWeakAreas] = useState(null);
  const [allWeakLoading, setAllWeakLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [supportForm, setSupportForm] = useState({ subject: "", focus: "", preferred_day_time: "", notes: "" });
  const [supportSending, setSupportSending] = useState(false);

  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [trendSubjectId, setTrendSubjectId] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("week");

  const readGate = (err) => {
    const status = err?.response?.status;
    if (status === 402) {
      return (
        err.response?.data?.data?.notification?.message ||
        err.response?.data?.message ||
        "A paid subscription is required to see your progress."
      );
    }
    if (status === 403 || status === 401) {
      return err.response?.data?.message || "Mobile access is not enabled for this account.";
    }
    return null;
  };

  useEffect(() => {
    api
      .get("mobile/dashboard/home/stats-today")
      .then((res) => setCompletedToday(Number(res.data?.data?.completed_today ?? 0)))
      .catch(() => {});

    api
      .get("mobile/exam-boards")
      .then((res) => setExamBoards(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setExamBoards([]));

    api
      .get("student/dashboard")
      .then((res) => {
        const d = res.data?.data ?? {};
        setPurchases({
          papers: Array.isArray(d.recent_paper_purchases) ? d.recent_paper_purchases : [],
          others: (d.latest_purchases ?? [])
            .filter((r) => r.resource_type !== "paper" && r.resource_type !== "pdf_pack")
            .slice(0, 6),
        });
      })
      .catch(() => {});

    api
      .get("student/games/leaderboard", { params: { days: 0 } })
      .then((res) => {
        const raw = res.data?.data?.leaderboard ?? res.data?.leaderboard ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const myId = userData?.id ?? userData?.user_id;
        const me = list.find((row) => String(row.id) === String(myId));
        setGameScore(me ? Number(me.total_points ?? 0) : 0);
      })
      .catch(() => setGameScore(0));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("mobile/dashboard/home/readiness")
      .then((res) => {
        if (!cancelled) setReadiness(res.data?.data?.readiness ?? null);
      })
      .catch((err) => {
        const gate = readGate(err);
        if (!cancelled && gate) setGateMessage(gate);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadBoardScoped = useCallback(async (boardId) => {
    setLoading(true);
    try {
      const params = boardId ? { exam_board_id: boardId } : {};
      const [overviewRes, statsRes] = await Promise.all([
        api.get("mobile/dashboard/home/overview"),
        api.get("mobile/dashboard/home/stats", { params }),
      ]);

      const overviewData = overviewRes.data?.data ?? {};
      const subjects = Array.isArray(overviewData.subject_wise_breakdown)
        ? overviewData.subject_wise_breakdown
        : [];
      setOverview(overviewData);
      setStats(statsRes.data?.data ?? null);

      const counts = await Promise.all(
        subjects.map((s) =>
          api
            .post("mobile/dashboard/subject", {
              subject_id: s.subject_id,
              include_areas: false,
              ...(boardId ? { exam_board_id: boardId } : {}),
            })
            .then((r) => [s.subject_id, r.data?.data?.subject_breakdown ?? null])
            .catch(() => [s.subject_id, null])
        )
      );
      setSubjectCounts(Object.fromEntries(counts));
    } catch (err) {
      const gate = readGate(err);
      if (gate) setGateMessage(gate);
      else toast.error("Could not load your dashboard. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoardScoped(examBoardId);
  }, [loadBoardScoped, examBoardId]);

  // Areas to improve: only fetched once a subject is opened, they are the expensive part.
  useEffect(() => {
    if (!drillSubject) return undefined;
    let cancelled = false;
    setAreasLoading(true);
    setAreas(null);
    api
      .post("mobile/dashboard/subject", {
        subject_id: drillSubject.subject_id,
        include_areas: true,
        ...(examBoardId ? { exam_board_id: examBoardId } : {}),
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data ?? {};
        const next = {
          weak_areas: d.weak_areas ?? [],
          medium_areas: d.medium_areas ?? [],
          best_areas: d.best_areas ?? [],
        };
        setAreas(next);
        // Open on the first band that has rows, not an empty "Needs Improvement" (4 Nov 2026).
        const first = AREA_BUCKETS.find((b) => next[b.key].length > 0);
        if (first) setDrillBucket(first.key);
      })
      .catch(() => {
        if (!cancelled) setAreas({ error: true, weak_areas: [], medium_areas: [], best_areas: [] });
      })
      .finally(() => {
        if (!cancelled) setAreasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drillSubject, examBoardId]);

  useEffect(() => {
    if (tab !== 2 || activity !== null) return;
    setActivityLoading(true);
    api
      .get("mobile/dashboard/progress/activity", { params: { limit: 60 } })
      .then((res) => setActivity(res.data?.data?.activity ?? []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [tab, activity]);

  // Daily Plan and Recommendations both work off weak areas across every subject, so fetch them
  // once, on first open — this is 1 request per subject and only worth paying for on demand.
  const loadAllWeakAreas = useCallback(async () => {
    if (allWeakAreas !== null || allWeakLoading) return;
    const subjects = Array.isArray(overview?.subject_wise_breakdown)
      ? overview.subject_wise_breakdown
      : [];
    if (subjects.length === 0) {
      setAllWeakAreas([]);
      return;
    }
    setAllWeakLoading(true);
    try {
      const results = await Promise.all(
        subjects.map((s) =>
          api
            .post("mobile/dashboard/subject", {
              subject_id: s.subject_id,
              include_areas: true,
              ...(examBoardId ? { exam_board_id: examBoardId } : {}),
            })
            .then((r) => {
              const d = r.data?.data ?? {};
              return [...(d.weak_areas ?? []), ...(d.medium_areas ?? [])].map((row) => ({
                ...row,
                subject_id: s.subject_id,
                subject_name: s.subject_name,
              }));
            })
            .catch(() => [])
        )
      );
      setAllWeakAreas(
        results.flat().sort((a, b) => (a.avg_percentage ?? 0) - (b.avg_percentage ?? 0))
      );
    } finally {
      setAllWeakLoading(false);
    }
  }, [allWeakAreas, allWeakLoading, overview, examBoardId]);

  const loadCatalog = useCallback(() => {
    if (catalog !== null) return;
    api
      .get("mobile/ai-tests", { params: examBoardId ? { exam_board_id: examBoardId } : {} })
      .then((res) => setCatalog(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setCatalog([]));
  }, [catalog, examBoardId]);

  const openAction = (key) => {
    setActionDialog(key);
    if (key === "plan" || key === "recommendations") {
      loadAllWeakAreas();
      loadCatalog();
    }
  };

  // Match an area to real worksheets: same subtopic first, then same topic, then a name match.
  const worksheetsForArea = useCallback(
    (area) => {
      if (!Array.isArray(catalog) || catalog.length === 0) return [];
      const areaSub = Number(area.subtopic_id) || 0;
      const areaTopic = Number(area.topic_id) || 0;
      const subName = String(area.subtopic_name || "").trim().toLowerCase();
      const topicName = String(area.topic_name || "").trim().toLowerCase();

      const bySubtopic = catalog.filter((w) => areaSub > 0 && Number(w.subtopic_id) === areaSub);
      if (bySubtopic.length > 0) return bySubtopic;

      const byName = subName
        ? catalog.filter(
            (w) =>
              String(w.subtopic_name || "").trim().toLowerCase() === subName ||
              String(w.name || "").toLowerCase().includes(subName)
          )
        : [];
      if (byName.length > 0) return byName;

      const byTopicId = catalog.filter((w) => areaTopic > 0 && Number(w.topic_id) === areaTopic);
      if (byTopicId.length > 0) return byTopicId;

      // The catalog's topic ids come from the master-topic tree, the areas' from the course tree,
      // so an exact topic name is the only bridge left for "more practice on this topic".
      return topicName
        ? catalog.filter((w) => String(w.topic_name || "").trim().toLowerCase() === topicName)
        : [];
    },
    [catalog]
  );

  const planRows = useMemo(() => {
    return (allWeakAreas ?? []).slice(0, 5).map((area) => ({
      area,
      worksheets: worksheetsForArea(area).slice(0, 3),
    }));
  }, [allWeakAreas, worksheetsForArea]);

  const recommendedWorksheets = useMemo(() => {
    const seen = new Set();
    const out = [];
    (allWeakAreas ?? []).forEach((area) => {
      worksheetsForArea(area).forEach((worksheet) => {
        if (seen.has(worksheet.id) || out.length >= 10) return;
        seen.add(worksheet.id);
        out.push({ ...worksheet, area });
      });
    });
    return out;
  }, [allWeakAreas, worksheetsForArea]);

  const submitRevisionSupport = async () => {
    if (!supportForm.subject.trim() || !supportForm.focus.trim()) {
      toast.info("Pick a subject and say what you would like help with.");
      return;
    }
    setSupportSending(true);
    try {
      await api.post("mobile/revision-support/request", {
        subject: supportForm.subject,
        focus: supportForm.focus,
        preferred_day_time: supportForm.preferred_day_time || null,
        additional_notes: supportForm.notes || null,
      });
      toast.success("Request sent. The team will get back to you.");
      setActionDialog(null);
      setSupportForm({ subject: "", focus: "", preferred_day_time: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not send your request. Try again later.");
    } finally {
      setSupportSending(false);
    }
  };

  const subjectRows = useMemo(() => {
    const rows = Array.isArray(overview?.subject_wise_breakdown)
      ? overview.subject_wise_breakdown
      : [];
    const scoreById = new Map(
      (stats?.avg_scores?.subjects ?? []).map((s) => [Number(s.subject_id), s])
    );
    const timeById = new Map(
      (stats?.time_spent?.subjects ?? []).map((s) => [Number(s.subject_id), s])
    );
    return rows.map((row) => ({
      ...row,
      ...subjectPalette(row.subject_name),
      score: scoreById.get(Number(row.subject_id)) ?? null,
      time: timeById.get(Number(row.subject_id)) ?? null,
      counts: subjectCounts[row.subject_id] ?? null,
    }));
  }, [overview, stats, subjectCounts]);

  const trendData = useMemo(() => {
    if (!Array.isArray(activity)) return [];
    const days = trendPeriod === "week" ? 7 : 30;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));

    const buckets = new Map();
    for (let i = 0; i < days; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = dayKey(d);
      buckets.set(key, {
        key,
        name:
          trendPeriod === "week"
            ? d.toLocaleDateString(undefined, { weekday: "short" })
            : String(d.getDate()),
        sum: 0,
        count: 0,
      });
    }

    activity.forEach((row) => {
      if (row.percentage == null || !row.completed_at) return;
      if (trendSubjectId && Number(row.subject_id) !== Number(trendSubjectId)) return;
      const bucket = buckets.get(dayKey(row.completed_at));
      if (!bucket) return;
      bucket.sum += Number(row.percentage);
      bucket.count += 1;
    });

    return Array.from(buckets.values()).map((b) => ({
      name: b.name,
      score: b.count > 0 ? Math.round(b.sum / b.count) : null,
    }));
  }, [activity, trendPeriod, trendSubjectId]);

  const trendAverage = useMemo(() => {
    const points = trendData.filter((p) => p.score != null);
    if (points.length === 0) return 0;
    return Math.round(points.reduce((sum, p) => sum + p.score, 0) / points.length);
  }, [trendData]);

  const filteredActivity = useMemo(() => {
    if (!Array.isArray(activity)) return [];
    if (!trendSubjectId) return activity;
    return activity.filter((row) => Number(row.subject_id) === Number(trendSubjectId));
  }, [activity, trendSubjectId]);

  // PDF packs are files, not a paper to sit, so they open their downloads instead.
  const [packDialog, setPackDialog] = useState(null);

  const handlePastPaperNavigate = (row) => {
    if (row.resource_type === "pdf_pack") {
      setPackDialog({ purchaseId: row.purchase_id, title: friendlyPackName(row.resource_name) });
      return;
    }
    const pid = row.purchase_id;
    if (pid != null && pid !== "") {
      navigate(`/${studentPrefix}/past-paper/${pid}/take`);
    } else {
      toast.info("Unable to open this practice paper. Try Practice Papers from the menu.");
    }
  };

  const handleLatestPurchaseNavigate = (row) => {
    if (row.resource_type === "mock_exam" && row.mock_exam_id != null) {
      navigate(
        `/${studentPrefix}/mock-exams?tab=exam&focusMockExam=${encodeURIComponent(row.mock_exam_id)}`
      );
    }
  };

  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const firstName = userData?.full_name?.split(" ")[0] || "Student";
  const completion = overview?.overall_completion ?? {};
  const timeSpent = stats?.time_spent ?? null;

  const quickLinkProgress = useMemo(() => {
    const worksheets = resourceProgress(sumResourceCounts(subjectRows, "worksheets"));
    const mocks = resourceProgress(sumResourceCounts(subjectRows, "mock_exams"));
    const papers = resourceProgress(sumResourceCounts(subjectRows, "papers"));
    return {
      worksheets,
      mocks,
      papers,
    };
  }, [subjectRows]);

  const quickLinks = [
    {
      label: "Work Sheet",
      icon: <WorksheetIcon />,
      color: "#673ab7",
      path: `/${studentPrefix}/ai-answer-sheet`,
      progress: quickLinkProgress.worksheets,
    },
    {
      label: "Mock Exams",
      icon: <TestIcon />,
      color: "#d32f2f",
      path: `/${studentPrefix}/mock-exams`,
      progress: quickLinkProgress.mocks,
    },
    {
      label: "Practice Papers",
      icon: <ArticleIcon />,
      color: "#1565c0",
      path: `/${studentPrefix}/past-papers`,
      progress: quickLinkProgress.papers,
    },
    { label: "Flash Cards", icon: <CollectionsBookmark />, color: "#009688", path: `/${studentPrefix}/flash-cards` },
    { label: "Games", icon: <TestIcon />, color: "#e91e63", path: `/${studentPrefix}/games` },
    { label: "Certificates", icon: <EmojiEvents />, color: "#d4af37", path: `/${studentPrefix}/certificates` },
  ];

  // A lapsed plan used to replace this whole page with a bare warning carrying the API's
  // "your plan period has ended" sentence — a dead end with no way forward. That sentence
  // belongs on the plan screen, where StudentSubscriptionLock already prints it under a
  // heading. Here we say only what the student can do next, and the dashboard stays.
  if (gateMessage) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", pt: 4 }}>
        <Alert
          severity="info"
          sx={{ borderRadius: "16px", fontWeight: 600 }}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${studentPrefix}/choose-plan`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Choose a plan
            </Button>
          }
        >
          A Mobile Plan unlocks your progress dashboard. Mock exams and practice papers you have
          bought stay open without one.
        </Alert>
      </Box>
    );
  }

  const examBoardFilter = examBoards.length > 0 && (
    <Box
      sx={{
        mt: { xs: 2, sm: 3 },
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mx: { xs: -0.5, sm: 0 },
        px: { xs: 0.5, sm: 0 },
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: "#374151", mr: 0.5, width: { xs: "100%", sm: "auto" } }}>
        Exam Boards
      </Typography>
      <Chip
        label="ALL"
        size="small"
        onClick={() => setExamBoardId(null)}
        sx={{
          fontWeight: 700,
          bgcolor: examBoardId === null ? ACCENT : "#f8f9fa",
          color: examBoardId === null ? "#fff" : "#2d3436",
          border: "1px solid #e5e7eb",
        }}
      />
      {examBoards.map((board) => (
        <Chip
          key={board.id}
          label={board.code}
          size="small"
          onClick={() => setExamBoardId(board.id)}
          sx={{
            fontWeight: 700,
            bgcolor: examBoardId === board.id ? ACCENT : "#f8f9fa",
            color: examBoardId === board.id ? "#fff" : "#2d3436",
            border: "1px solid #e5e7eb",
          }}
        />
      ))}
    </Box>
  );

  const renderHome = () => (
    <>
      {examBoardFilter}

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={8}>
          <SectionHeading icon={<ReadinessIcon sx={{ color: ACCENT }} />}>Readiness Meter</SectionHeading>
          <Card sx={{ ...CARD_SX, mb: { xs: 3, sm: 4 } }}>
            <CardContent sx={{ p: { xs: "16px !important", sm: "24px !important" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#636e72" }}>
                  Global Readiness
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.6rem" }, color: ACCENT }}>
                  {pct(readiness?.overall)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct(readiness?.overall)}
                sx={{
                  height: { xs: 8, sm: 10 },
                  borderRadius: 5,
                  bgcolor: "#f1f2f6",
                  "& .MuiLinearProgress-bar": { borderRadius: 5, background: GRADIENT },
                }}
              />
              <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 600, display: "block", mt: 1 }}>
                Averaged across your completed worksheets, mock exams and practice papers.
              </Typography>

              {Array.isArray(readiness?.schools) && readiness.schools.length > 0 ? (
                <>
                  <Divider sx={{ my: { xs: 2, sm: 2.5 } }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: "#111827", mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <SchoolIcon sx={{ fontSize: 18, color: ACCENT }} /> Readiness by target school
                  </Typography>
                  {readiness.schools.map((school) => (
                    <ProgressRow
                      key={school.school_id}
                      label={school.school_name}
                      value={school.average_score}
                      right={`${pct(school.average_score)}%`}
                      color="#667eea"
                    />
                  ))}
                </>
              ) : null}
            </CardContent>
          </Card>

          <SectionHeading icon={<TrendingUp sx={{ color: "#667eea" }} />}>Your Subjects</SectionHeading>
          {loading && subjectRows.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress thickness={5} size={44} sx={{ color: ACCENT }} />
            </Box>
          ) : subjectRows.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: "20px", fontWeight: 600 }}>
              No subjects enabled yet. Enable subjects from your profile to start tracking progress.
            </Alert>
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
              {subjectRows.map((subject) => {
                const worksheets = resourceProgress(subject.counts?.worksheets);
                const mocks = resourceProgress(subject.counts?.mock_exams);
                const papers = resourceProgress(subject.counts?.papers);
                const resourceRows = [
                  { key: "worksheets", label: "Worksheets", data: worksheets, color: "#673ab7" },
                  { key: "mocks", label: "Mock exams", data: mocks, color: "#d32f2f" },
                  { key: "papers", label: "Practice papers", data: papers, color: "#1565c0" },
                ];
                return (
                  <Grid item xs={12} sm={6} key={subject.subject_id}>
                    <Card sx={{ ...CARD_SX, height: "100%" }}>
                      <CardContent sx={{ p: { xs: "14px !important", sm: "20px !important" } }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1.5,
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: subject.color, flexShrink: 0 }} />
                            <Typography sx={{ fontWeight: 800, color: "#111827", fontSize: { xs: "0.95rem", sm: "1rem" } }} noWrap>
                              {subject.subject_name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            {subject.time?.total_seconds > 0 ? (
                              <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 700 }}>
                                {subject.time.total}
                              </Typography>
                            ) : null}
                            {subject.score ? (
                              <Chip
                                size="small"
                                label={`${subject.score.average_score}% · SAS ${subject.score.sas_score}`}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.68rem",
                                  bgcolor: subject.tint,
                                  color: subject.color,
                                  height: { xs: 22, sm: 24 },
                                }}
                              />
                            ) : null}
                          </Box>
                        </Box>

                        {resourceRows.map((row) => (
                          <ProgressRow
                            key={row.key}
                            label={row.label}
                            value={row.data.percent}
                            right={row.data.total != null ? `${row.data.fraction} completed` : "—"}
                            color={row.color}
                            status={row.data.status}
                            statusColor={row.data.statusColor}
                            compact={isXs}
                          />
                        ))}
                        <ProgressRow
                          label="Overall"
                          value={subject.content_completion_percentage}
                          right={`${pct(subject.content_completion_percentage)}%`}
                          color={subject.color}
                          compact={isXs}
                        />

                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          {[
                            { label: "Worksheets", data: worksheets, color: "#673ab7" },
                            { label: "Mocks", data: mocks, color: "#d32f2f" },
                            { label: "Papers", data: papers, color: "#1565c0" },
                          ].map((cell) => (
                            <Grid item xs={4} key={cell.label}>
                              <Box
                                sx={{
                                  p: { xs: 1, sm: 1.25 },
                                  bgcolor: "#f8f9fa",
                                  borderRadius: { xs: "12px", sm: "14px" },
                                  textAlign: "center",
                                  border: "1px solid #eee",
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: cell.color,
                                    fontSize: { xs: "0.85rem", sm: "1.05rem" },
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {cell.data.total != null ? cell.data.fraction : "—"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#636e72",
                                    fontWeight: 600,
                                    fontSize: { xs: "0.58rem", sm: "0.68rem" },
                                    display: "block",
                                  }}
                                >
                                  {cell.label}
                                </Typography>
                                {cell.data.status ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: cell.data.statusColor,
                                      fontWeight: 800,
                                      fontSize: { xs: "0.52rem", sm: "0.6rem" },
                                      display: "block",
                                      mt: 0.25,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {cell.data.status}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        <Button
                          fullWidth
                          size="small"
                          onClick={() => {
                            setDrillSubject(subject);
                            setDrillBucket("weak_areas");
                            setTab(1);
                          }}
                          sx={{
                            mt: 1.5,
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: subject.color,
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                          }}
                        >
                          View areas to improve
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Box sx={{ mt: { xs: 3, sm: 0 } }}>
          <SectionHeading icon={<TestIcon sx={{ color: "#e91e63" }} />}>
            Interactive Learning Games
            {gameScore !== null && (
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: "#e91e63",
                  fontSize: { xs: "0.75rem", sm: "0.9rem" },
                  ml: { xs: 0, sm: 1 },
                  display: { xs: "block", sm: "inline" },
                }}
              >
                Your score: {gameScore.toLocaleString()} pts
              </Box>
            )}
          </SectionHeading>
          </Box>
          <Grid container spacing={{ xs: 1.25, sm: 2 }}>
            {[
              { key: "maths", label: "Maths", color: "#1976d2" },
              { key: "english", label: "English", color: "#c2185b" },
              { key: "verbal-reasoning", label: "Verbal Reasoning", color: "#8e24aa" },
              { key: "non-verbal-reasoning", label: "Non-Verbal Reasoning", color: "#ff9800" },
            ].map((subject) => (
              <Grid item xs={6} sm={6} md={3} key={subject.key}>
                <Button
                  fullWidth
                  onClick={() => navigate(`/${studentPrefix}/games/${subject.key}`)}
                  sx={{
                    py: { xs: 1.25, sm: 1.5 },
                    px: 1,
                    borderRadius: "18px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: { xs: "0.72rem", sm: "0.8rem" },
                    color: subject.color,
                    border: `1px solid ${subject.color}30`,
                    background: `${subject.color}05`,
                    lineHeight: 1.25,
                    minHeight: { xs: 48, sm: 52 },
                    "&:hover": {
                      background: subject.color,
                      color: "white",
                      transform: "translateY(-2px)",
                      boxShadow: `0 10px 20px ${subject.color}40`,
                    },
                    transition: "all 0.25s ease",
                  }}
                >
                  {subject.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#111827", mb: 2, fontSize: { xs: "1.05rem", sm: "1.25rem" }, mt: { xs: 1, lg: 0 } }}
          >
            Quick Links
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            {quickLinks.map((item) => (
              <Grid item xs={6} key={item.label}>
                <Button
                  fullWidth
                  onClick={() => navigate(item.path)}
                  sx={{
                    py: { xs: 1, sm: 1.25 },
                    px: { xs: 0.75, sm: 1 },
                    borderRadius: { xs: "14px", sm: "16px" },
                    background: "white",
                    color: "#111827",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    border: "1px solid #f0f0f0",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.72rem", sm: "0.8rem" },
                    minHeight: { xs: item.progress?.status ? 78 : 56, sm: "auto" },
                    "&:hover": {
                      background: item.color,
                      color: "white",
                      "& .MuiSvgIcon-root": { color: "white" },
                      "& .quick-link-progress": { color: "rgba(255,255,255,0.9) !important" },
                      "& .quick-link-status": { color: "rgba(255,255,255,0.85) !important", bgcolor: "rgba(255,255,255,0.18) !important" },
                    },
                    transition: "all 0.25s ease",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1 }, width: "100%" }}>
                    <Box
                      sx={{
                        color: item.color,
                        background: `${item.color}10`,
                        p: { xs: 0.5, sm: 0.75 },
                        borderRadius: "10px",
                        display: "flex",
                        flexShrink: 0,
                        "& .MuiSvgIcon-root": { fontSize: { xs: 18, sm: 24 } },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.72rem", sm: "0.8rem" },
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.progress?.total != null ? (
                        <Typography
                          className="quick-link-progress"
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: item.color,
                            display: "block",
                            lineHeight: 1.2,
                            fontSize: { xs: "0.6rem", sm: "0.75rem" },
                          }}
                        >
                          {item.progress.fraction} completed
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  {item.progress?.status ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, width: "100%" }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct(item.progress.percent)}
                        sx={{
                          flex: 1,
                          height: 4,
                          borderRadius: 4,
                          bgcolor: `${item.color}18`,
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            bgcolor: item.color,
                          },
                        }}
                      />
                      <Chip
                        className="quick-link-status"
                        size="small"
                        label={item.progress.status}
                        sx={{
                          height: 16,
                          fontSize: { xs: "0.5rem", sm: "0.55rem" },
                          fontWeight: 800,
                          bgcolor: `${item.progress.statusColor}18`,
                          color: item.progress.statusColor,
                          maxWidth: { xs: 72, sm: "none" },
                          "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
                        }}
                      />
                    </Box>
                  ) : null}
                </Button>
              </Grid>
            ))}
          </Grid>

          <SectionHeading
            icon={<ArticleIcon sx={{ color: "#00695c" }} />}
            action={
              <Button component={Link} to={`/${studentPrefix}/past-papers`} size="small" sx={{ fontWeight: 700 }}>
                View All
              </Button>
            }
          >
            Practice Papers
          </SectionHeading>
          <PackDownloadsDialog
            purchaseId={packDialog?.purchaseId}
            title={packDialog?.title}
            onClose={() => setPackDialog(null)}
          />
          <Card sx={{ ...CARD_SX, mb: 4 }}>
            <CardContent sx={{ p: "16px !important" }}>
              {purchases.papers.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2.5, px: 1.5 }}>
                  <Avatar sx={{ bgcolor: "#e0f2f1", color: "#00695c", width: 52, height: 52, mx: "auto", mb: 1.5 }}>
                    <ArticleIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#111827", mb: 0.5 }}>
                    Your practice papers will live here
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, mb: 2, lineHeight: 1.6 }}>
                    Real exam-style papers with full mark schemes. When a parent buys one for you, it will appear here ready to start.
                  </Typography>
                  <Button
                    component={Link}
                    to={`/${studentPrefix}/past-papers`}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: "999px", textTransform: "none", px: 2.5 }}
                  >
                    Browse practice papers
                  </Button>
                </Box>
              ) : (
                <List disablePadding dense>
                  {purchases.papers.map((row, idx) => {
                    const isPack = row.resource_type === "pdf_pack";
                    const { family, title } = splitPackName(row.resource_name);
                    return (
                      <React.Fragment key={`paper-${row.purchase_id ?? idx}-${idx}`}>
                        {idx > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
                        {/* Same row as the course dashboard's: icon, paper name, school, then the action. */}
                        <ListItemButton
                          onClick={() => handlePastPaperNavigate(row)}
                          sx={{ borderRadius: "14px", py: 1.25, px: 1, gap: 1.5 }}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{ bgcolor: isPack ? "#ede7f6" : "#e0f2f1", color: isPack ? "#5e35b1" : "#00695c", width: 40, height: 40, borderRadius: "12px" }}
                          >
                            {isPack ? <DownloadIcon fontSize="small" /> : <ArticleIcon fontSize="small" />}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
                              {title}
                            </Typography>
                            {family ? (
                              <Typography variant="caption" sx={{ color: "#4b5563", fontWeight: 600, display: "block", lineHeight: 1.4 }} noWrap title={family}>
                                {family}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ color: "#9ca3af", fontWeight: 600, display: "block" }}>
                              {isPack ? "PDF download" : "Online paper"}
                              {row.purchased_at
                                ? ` · Added ${new Date(row.purchased_at).toLocaleDateString(undefined, { dateStyle: "medium" })}`
                                : ""}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={isPack ? "Download" : "Start"}
                            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 24, bgcolor: "#e0f2f1", color: "#00695c", cursor: "pointer", flexShrink: 0 }}
                          />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>

          <SectionHeading
            icon={<ShoppingBagIcon sx={{ color: "#00897b" }} />}
            action={
              <Button component={Link} to={`/${studentPrefix}/mock-exams?tab=exam`} size="small" sx={{ fontWeight: 700 }}>
                View All
              </Button>
            }
          >
            Latest purchases
          </SectionHeading>
          <Card sx={CARD_SX}>
            <CardContent sx={{ p: "16px !important" }}>
              {purchases.others.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600, py: 1, px: 0.5 }}>
                  No recent mock exam purchases yet.
                </Typography>
              ) : (
                <List disablePadding dense>
                  {purchases.others.map((row, idx) => (
                    <React.Fragment key={`${row.resource_type}-${row.mock_exam_id ?? idx}-${idx}`}>
                      {idx > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
                      <ListItemButton
                        onClick={() => handleLatestPurchaseNavigate(row)}
                        alignItems="flex-start"
                        sx={{ borderRadius: "12px", py: 1.25, px: 1, gap: 1 }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                            <Chip
                              size="small"
                              label={row.resource_type_label || row.resource_type}
                              sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22, bgcolor: "#e0f2f1", color: "#00695c" }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
                              {row.resource_name}
                            </Typography>
                          </Box>
                          {row.purchased_at ? (
                            <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 600, display: "block" }}>
                              Purchased{" "}
                              {new Date(row.purchased_at).toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </Typography>
                          ) : null}
                        </Box>
                        <ChevronRight sx={{ color: "#374151", mt: 0.25, flexShrink: 0 }} fontSize="small" />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {(stats?.completed_today_detail?.items ?? []).length > 0 && (
            <>
              <Box sx={{ mt: 4 }}>
                <SectionHeading icon={<CheckCircleOutline sx={{ color: "#16a34a" }} />}>
                  Completed today
                </SectionHeading>
              </Box>
              <Card sx={CARD_SX}>
                <CardContent sx={{ p: "16px !important" }}>
                  <Stack spacing={1}>
                    {stats.completed_today_detail.items.map((task, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Timer sx={{ fontSize: 16, color: "#95a5a6" }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#2c3e50" }}>
                          {task.title || task.name || task.resource_name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );

  const renderImproveList = () => (
    <Grid container spacing={3} sx={{ mt: 0.5 }}>
      <Grid item xs={12} lg={8}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 800, color: ACCENT, letterSpacing: "0.06em", textTransform: "uppercase" }}
        >
          Areas to improve
        </Typography>
        <Card sx={{ ...CARD_SX, mt: 1.5 }}>
          <CardContent sx={{ p: "24px !important" }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827" }}>
              Subjects
            </Typography>
            <Typography variant="body2" sx={{ color: "#636e72", mb: 2 }}>
              Pick a subject to view weak, medium &amp; best areas.
            </Typography>
            {subjectRows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: "16px", fontWeight: 600 }}>
                No subjects enabled yet.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {subjectRows.map((subject) => (
                  <ListItemButton
                    key={subject.subject_id}
                    onClick={() => {
                      setDrillSubject(subject);
                      setDrillBucket("weak_areas");
                    }}
                    sx={{
                      borderRadius: "16px",
                      bgcolor: subject.tint,
                      py: 1.75,
                      "&:hover": { bgcolor: subject.tint, filter: "brightness(0.97)" },
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: subject.color, mr: 1.5 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                        {subject.subject_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600 }}>
                        {pct(subject.content_completion_percentage)}% overall progress
                      </Typography>
                    </Box>
                    <ChevronRight sx={{ color: "#7f8c8d" }} />
                  </ListItemButton>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: ACCENT,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            display: "block",
            mt: 4,
            mb: 1.5,
          }}
        >
          Actions
        </Typography>
        <Stack spacing={1.5}>
          {[
            {
              key: "plan",
              icon: <AutoAwesome sx={{ color: ACCENT }} />,
              bg: "#ede9fe",
              title: "Daily Plan",
              subtitle: "Today's practice, picked from your weakest areas",
            },
            {
              key: "support",
              icon: <SupportAgent sx={{ color: "#2563eb" }} />,
              bg: "#dbeafe",
              title: "Revision Support",
              subtitle: "Request a tutor or find help resources",
            },
            {
              key: "recommendations",
              icon: <RecommendIcon sx={{ color: "#00897b" }} />,
              bg: "#cffafe",
              title: "Recommendations",
              subtitle: "Worksheets matched to your weak areas",
            },
          ].map((action) => (
            <ListItemButton
              key={action.key}
              onClick={() => openAction(action.key)}
              sx={{ ...CARD_SX, borderRadius: "20px", bgcolor: "#fff", py: 1.75, px: 2 }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  bgcolor: action.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                {action.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: "#111827" }}>{action.title}</Typography>
                <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600 }}>
                  {action.subtitle}
                </Typography>
              </Box>
              <ChevronRight sx={{ color: "#7f8c8d" }} />
            </ListItemButton>
          ))}
        </Stack>
      </Grid>
    </Grid>
  );

  const renderImproveDrill = () => {
    const rows = areas?.[drillBucket] ?? [];
    const bucketMeta = AREA_BUCKETS.find((b) => b.key === drillBucket);
    return (
      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <IconButton
              onClick={() => setDrillSubject(null)}
              sx={{ bgcolor: "#f8f9fa", "&:hover": { bgcolor: "#eef0f4" } }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Chip
              label={drillSubject.subject_name}
              sx={{
                fontWeight: 800,
                bgcolor: drillSubject.tint,
                color: drillSubject.color,
                borderRadius: "12px",
              }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827", mb: 2, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            Areas to improve
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              p: 0.625,
              bgcolor: "#f1f2f6",
              borderRadius: "18px",
              mb: 2.5,
            }}
          >
            {AREA_BUCKETS.map((bucket) => {
              const active = drillBucket === bucket.key;
              return (
                <Box
                  key={bucket.key}
                  onClick={() => setDrillBucket(bucket.key)}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    py: 1,
                    borderRadius: "14px",
                    cursor: "pointer",
                    bgcolor: active ? "#fff" : "transparent",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: active ? ACCENT : "#636e72",
                      display: "block",
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      px: 0.25,
                      lineHeight: 1.2,
                    }}
                  >
                    {isXs ? bucket.short : bucket.label}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: active ? ACCENT : "#95a5a6" }}>
                    {areas ? (areas[bucket.key] ?? []).length : "—"}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Card sx={CARD_SX}>
            <CardContent sx={{ p: "8px !important" }}>
              {areasLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                  <CircularProgress thickness={5} size={36} sx={{ color: ACCENT }} />
                </Box>
              ) : areas?.error ? (
                <Alert severity="error" sx={{ m: 1 }}>
                  Could not load your topics. Please close this subject and open it again.
                </Alert>
              ) : rows.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#636e72", fontWeight: 600, p: 3, textAlign: "center" }}>
                  Complete more worksheets in this subject to see topics here.
                </Typography>
              ) : (
                <List disablePadding>
                  {rows.map((row, idx) => (
                    <React.Fragment key={`${row.topic_id}-${row.subtopic_id ?? idx}`}>
                      {idx > 0 ? <Divider /> : null}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1, sm: 2 }, px: { xs: 1.25, sm: 2 }, py: 1.75 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#111827",
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.subtopic_name || row.topic_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600, display: "block" }} noWrap>
                            {row.topic_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#95a5a6",
                              display: { xs: "none", sm: "block" },
                            }}
                          >
                            Latest attempt per resource · avg across {row.attempts}{" "}
                            {row.attempts === 1 ? "resource" : "resources"}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                          <Chip
                            size="small"
                            label={`${pct(row.avg_percentage)}%`}
                            sx={{
                              fontWeight: 800,
                              bgcolor: bucketMeta?.bg,
                              color: bucketMeta?.fg,
                            }}
                          />
                          {row.sas_score != null ? (
                            <Typography variant="caption" sx={{ display: "block", color: "#95a5a6", fontWeight: 700 }}>
                              SAS {row.sas_score}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderProgress = () => {
    const remaining = Math.max(
      0,
      Number(completion.total_items ?? 0) - Number(completion.completed_items ?? 0)
    );
    return (
      <>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { background: "#e5e7eb", borderRadius: 3 },
          }}
        >
          <Chip
            label="All"
            onClick={() => setTrendSubjectId(null)}
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              bgcolor: trendSubjectId === null ? ACCENT : "#f8f9fa",
              color: trendSubjectId === null ? "#fff" : "#2d3436",
            }}
          />
          {subjectRows.map((subject) => (
            <Chip
              key={subject.subject_id}
              label={subject.subject_name}
              onClick={() => setTrendSubjectId(subject.subject_id)}
              sx={{
                flexShrink: 0,
                fontWeight: 700,
                bgcolor: trendSubjectId === subject.subject_id ? ACCENT : "#f8f9fa",
                color: trendSubjectId === subject.subject_id ? "#fff" : "#2d3436",
              }}
            />
          ))}
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 0.5 }}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ ...CARD_SX, mb: 3 }}>
              <CardContent sx={{ p: { xs: "16px !important", sm: "24px !important" } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    mb: 2,
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
                      Score Trend
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600, display: "block" }}>
                      Subject:{" "}
                      {trendSubjectId
                        ? subjectRows.find((s) => s.subject_id === trendSubjectId)?.subject_name
                        : "All subjects"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600 }}>
                      Average: {trendAverage}%
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      p: 0.375,
                      bgcolor: "#f1f2f6",
                      borderRadius: "12px",
                      alignSelf: { xs: "stretch", sm: "flex-start" },
                    }}
                  >
                    {["week", "month"].map((period) => (
                      <Box
                        key={period}
                        onClick={() => setTrendPeriod(period)}
                        sx={{
                          flex: { xs: 1, sm: "none" },
                          textAlign: "center",
                          px: 2,
                          py: 0.75,
                          borderRadius: "10px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          textTransform: "capitalize",
                          bgcolor: trendPeriod === period ? "#fff" : "transparent",
                          color: trendPeriod === period ? ACCENT : "#636e72",
                          boxShadow: trendPeriod === period ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                        }}
                      >
                        {period}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ height: { xs: 200, sm: 260 }, width: "100%" }}>
                  {activityLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                      <CircularProgress thickness={5} size={36} sx={{ color: ACCENT }} />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 10, right: isXs ? 8 : 20, left: isXs ? -18 : 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="2 4" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#636e72", fontSize: isXs ? 10 : 11, fontWeight: 600 }}
                          interval={trendPeriod === "week" ? 0 : 3}
                        />
                        <YAxis
                          domain={[0, 100]}
                          ticks={[0, 25, 50, 75, 100]}
                          axisLine={false}
                          tickLine={false}
                          width={isXs ? 32 : 40}
                          tick={{ fill: "#636e72", fontSize: isXs ? 10 : 11, fontWeight: 600 }}
                        />
                        <ChartTooltip
                          formatter={(value) => [`${value}%`, "Score"]}
                          contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 8px 30px rgba(0,0,0,0.1)" }}
                        />
                        <Line
                          type="monotone"
                          dataKey="score"
                          stroke={ACCENT}
                          strokeWidth={3}
                          dot={{ r: isXs ? 3 : 4, fill: ACCENT }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </Box>
                {trendData.every((p) => p.score == null) && !activityLoading ? (
                  <Typography variant="body2" sx={{ color: "#636e72", fontWeight: 600, mt: 1 }}>
                    No scores in this period yet.
                  </Typography>
                ) : null}
              </CardContent>
            </Card>

            <Card sx={{ ...CARD_SX, mb: 3 }}>
              <CardContent sx={{ p: { xs: "14px !important", sm: "24px !important" } }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "#111827", mb: 2, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}
                >
                  Subject Progress
                </Typography>
                <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
                  {subjectRows.map((subject) => {
                    const worksheets = resourceProgress(subject.counts?.worksheets);
                    const mocks = resourceProgress(subject.counts?.mock_exams);
                    const papers = resourceProgress(subject.counts?.papers);
                    return (
                      <Grid item xs={12} sm={6} key={subject.subject_id}>
                        <Box
                          sx={{
                            height: "100%",
                            p: { xs: 1.75, sm: 1.75 },
                            borderRadius: { xs: "14px", sm: "16px" },
                            border: "1px solid #f0f0f0",
                            bgcolor: "#fafafa",
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: subject.color, flexShrink: 0 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: "#111827",
                                minWidth: 0,
                                fontSize: { xs: "0.9rem", sm: "0.875rem" },
                              }}
                              noWrap
                            >
                              {subject.subject_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#95a5a6",
                                fontWeight: 700,
                                ml: "auto",
                                flexShrink: 0,
                                fontSize: { xs: "0.75rem", sm: "0.75rem" },
                              }}
                            >
                              {pct(subject.content_completion_percentage)}% overall
                            </Typography>
                          </Box>
                          <ProgressRow
                            label="Worksheets"
                            value={worksheets.percent}
                            right={worksheets.total != null ? `${worksheets.fraction} completed` : "—"}
                            color="#673ab7"
                            status={worksheets.status}
                            statusColor={worksheets.statusColor}
                            compact={!isXs}
                          />
                          <ProgressRow
                            label="Mock exams"
                            value={mocks.percent}
                            right={mocks.total != null ? `${mocks.fraction} completed` : "—"}
                            color="#d32f2f"
                            status={mocks.status}
                            statusColor={mocks.statusColor}
                            compact={!isXs}
                          />
                          <ProgressRow
                            label="Practice papers"
                            value={papers.percent}
                            right={papers.total != null ? `${papers.fraction} completed` : "—"}
                            color="#1565c0"
                            status={papers.status}
                            statusColor={papers.statusColor}
                            compact={!isXs}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 2.5 }}>
              {[
                { value: completion.completed_items ?? 0, label: "Completed" },
                { value: `${pct(completion.completion_percentage)}%`, label: "Completion" },
                { value: remaining, label: "Remaining" },
              ].map((tile) => (
                <Grid item xs={4} key={tile.label}>
                  <Box
                    sx={{
                      bgcolor: "#ede9fe",
                      borderRadius: { xs: "12px", sm: "16px" },
                      p: { xs: 1.25, sm: 1.5 },
                      textAlign: "center",
                      height: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.05rem", sm: "1.35rem" },
                        color: ACCENT,
                        lineHeight: 1.2,
                      }}
                    >
                      {tile.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#636e72",
                        fontWeight: 700,
                        fontSize: { xs: "0.58rem", sm: "0.65rem" },
                        lineHeight: 1.2,
                        display: "block",
                      }}
                    >
                      {tile.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#111827", mb: 2, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}
            >
              Recent Activity
            </Typography>
            <Card sx={CARD_SX}>
              <CardContent sx={{ p: "8px !important" }}>
                {activityLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress thickness={5} size={36} sx={{ color: ACCENT }} />
                  </Box>
                ) : filteredActivity.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#636e72", fontWeight: 600, p: 3, textAlign: "center" }}>
                    Nothing completed yet.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {filteredActivity.slice(0, RECENT_ACTIVITY_LIMIT).map((row, idx) => (
                      <React.Fragment key={`${row.type}-${row.resource_id}-${idx}`}>
                        {idx > 0 ? <Divider /> : null}
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, px: { xs: 1.25, sm: 1.5 }, py: 1.5 }}>
                          <Box
                            sx={{
                              width: { xs: 30, sm: 34 },
                              height: { xs: 30, sm: 34 },
                              borderRadius: "12px",
                              bgcolor: "#f8f9fa",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <InsertDriveFileOutlined sx={{ fontSize: 18, color: "#7f8c8d" }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#111827",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                              }}
                            >
                              {row.title || row.topic_name || row.type_label}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                                {row.type_label}
                                {row.completed_at
                                  ? ` · ${new Date(row.completed_at).toLocaleDateString(undefined, { dateStyle: "medium" })}`
                                  : ""}
                              </Typography>
                              {row.is_practice ? (
                                <Chip
                                  size="small"
                                  label="Practice"
                                  sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#d97706" }}
                                />
                              ) : null}
                            </Box>
                          </Box>
                          {row.percentage != null ? (
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                flexShrink: 0,
                                color: row.percentage >= 80 ? "#16a34a" : row.percentage >= 40 ? "#d97706" : "#dc2626",
                              }}
                            >
                              {pct(row.percentage)}%
                            </Typography>
                          ) : null}
                        </Box>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        pb: { xs: 3, sm: 4 },
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1.25, sm: 2, md: 0 },
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      {onTrial ? (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: "16px", alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${studentPrefix}/choose-plan?plans=1`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Choose a plan
            </Button>
          }
        >
          <Typography sx={{ fontWeight: 700 }}>
            You're on a free trial{trialEndsLabel ? ` — it ends ${trialEndsLabel}` : ""}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            Everything is open during your trial. Choose a plan before it ends to keep access — when the trial ends, this opens on its own.
          </Typography>
        </Alert>
      ) : null}
      <GettingStartedCard role="student" />
      <Box
        sx={{
          background: GRADIENT,
          borderRadius: { xs: "18px", sm: "24px" },
          color: "white",
          px: { xs: 2, sm: 2.5, md: 4 },
          pt: { xs: 2.5, sm: 3, md: 4 },
          pb: { xs: 4.5, sm: 5, md: 6 },
          boxShadow: "0 15px 35px rgba(102,126,234,0.3)",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.9rem" }, mb: 1 }}>
          Hi, {firstName}! 👋
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-block",
            px: { xs: 1.5, sm: 2 },
            py: 0.75,
            borderRadius: "999px",
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.25)",
            fontWeight: 600,
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
          }}
        >
          Done {completion.completed_items ?? 0}/{completion.total_items ?? 0}
        </Box>
      </Box>

      <Card sx={{ ...CARD_SX, mt: { xs: -3, sm: -4 }, mx: { xs: 0.5, sm: 1, md: 3 }, position: "relative" }}>
        <CardContent sx={{ p: { xs: "12px !important", sm: "20px !important" } }}>
          <Grid container rowSpacing={{ xs: 1, sm: 0 }}>
            <Grid item xs={6} md={3}>
              <StatTile label="Today" value={completedToday ?? "—"} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Avg score" value={`${stats?.global_avg_score ?? 0}%`} color={ACCENT} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="SAS"
                value={stats?.global_sas_score ?? "—"}
                caption={stats?.global_sas_band_label || ""}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Time spent"
                value={timeSpent?.total ?? "0m"}
                caption={(timeSpent?.subjects ?? [])
                  .filter((s) => s.total_seconds > 0)
                  .slice(0, isXs ? 1 : 2)
                  .map((s) => `${s.subject_name} ${s.total}`)
                  .join(" · ")}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ mt: { xs: 2, sm: 3 }, borderBottom: "1px solid #f0f0f0", mx: { xs: -0.5, sm: 0 } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isXs ? "fullWidth" : "scrollable"}
          scrollButtons={isXs ? false : "auto"}
          allowScrollButtonsMobile
          sx={{
            minHeight: { xs: 44, sm: 48 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              minHeight: { xs: 44, sm: 48 },
              minWidth: { xs: 0, sm: 90 },
              color: "#7f8c8d",
              gap: 0.5,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              px: { xs: 0.5, sm: 2 },
            },
            "& .Mui-selected": { color: ACCENT },
            "& .MuiTabs-indicator": { backgroundColor: ACCENT, height: 3, borderRadius: 3 },
          }}
        >
          <Tab icon={<HomeIcon fontSize="small" />} iconPosition="start" label="Home" />
          <Tab icon={<ImproveIcon fontSize="small" />} iconPosition="start" label="Improve" />
          <Tab icon={<ProgressIcon fontSize="small" />} iconPosition="start" label="Progress" />
        </Tabs>
      </Box>

      {tab === 0 && renderHome()}
      {tab === 1 && (drillSubject ? renderImproveDrill() : renderImproveList())}
      {tab === 2 && renderProgress()}

      <Dialog
        open={actionDialog === "plan" || actionDialog === "recommendations"}
        onClose={() => setActionDialog(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isXs}
        PaperProps={{ sx: { borderRadius: isXs ? 0 : "20px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {actionDialog === "plan" ? "Daily Plan" : "Recommendations"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "#636e72", mb: 2 }}>
            {actionDialog === "plan"
              ? "Your five weakest areas right now. Work through them in order — each one links to worksheets on that topic."
              : "Worksheets matched to the areas you score lowest on."}
          </Typography>
          {allWeakLoading || catalog === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress thickness={5} size={36} sx={{ color: ACCENT }} />
            </Box>
          ) : (allWeakAreas ?? []).length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: "16px", fontWeight: 600 }}>
              Complete a few worksheets first — once you have some scores we can point you at the
              topics worth practising.
            </Alert>
          ) : actionDialog === "plan" ? (
            <Stack spacing={2}>
              {planRows.map(({ area, worksheets }, idx) => {
                const term = area.subtopic_name || area.topic_name || "";
                return (
                  <Box key={`${area.subject_id}-${area.topic_id}-${area.subtopic_id ?? idx}`}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Chip
                        size="small"
                        label={`${idx + 1}`}
                        sx={{ fontWeight: 800, bgcolor: "#ede9fe", color: ACCENT, minWidth: 28 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, color: "#111827" }}>{term}</Typography>
                        <Typography variant="caption" sx={{ color: "#636e72", fontWeight: 600 }}>
                          {area.subject_name}
                          {area.topic_name && area.topic_name !== term ? ` · ${area.topic_name}` : ""}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${pct(area.avg_percentage)}%`}
                        sx={{
                          fontWeight: 800,
                          bgcolor: area.avg_percentage < 40 ? "#fee2e2" : "#fef3c7",
                          color: area.avg_percentage < 40 ? "#dc2626" : "#d97706",
                        }}
                      />
                    </Box>
                    {worksheets.length === 0 ? (
                      <Typography variant="caption" sx={{ color: "#95a5a6", pl: { xs: 0, sm: 5 } }}>
                        No worksheet available on this topic yet.
                      </Typography>
                    ) : (
                      <Stack spacing={0.75} sx={{ pl: { xs: 0, sm: 5 } }}>
                        {worksheets.map((worksheet) => (
                          <WorksheetRow
                            key={worksheet.id}
                            worksheet={worksheet}
                            onOpen={() => {
                              setActionDialog(null);
                              navigate(`/${studentPrefix}/work-sheet/${worksheet.id}/take`);
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          ) : recommendedWorksheets.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: "16px", fontWeight: 600 }}>
              No worksheets available yet on the topics you score lowest on.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {recommendedWorksheets.map((worksheet) => (
                <WorksheetRow
                  key={worksheet.id}
                  worksheet={worksheet}
                  areaLabel={`${worksheet.area.subject_name} · ${pct(worksheet.area.avg_percentage)}% on ${
                    worksheet.area.subtopic_name || worksheet.area.topic_name
                  }`}
                  onOpen={() => {
                    setActionDialog(null);
                    navigate(`/${studentPrefix}/work-sheet/${worksheet.id}/take`);
                  }}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setActionDialog(null)} sx={{ textTransform: "none", fontWeight: 700 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={actionDialog === "support"}
        onClose={() => (supportSending ? null : setActionDialog(null))}
        maxWidth="sm"
        fullWidth
        fullScreen={isXs}
        PaperProps={{ sx: { borderRadius: isXs ? 0 : "20px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Revision Support</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "#636e72", mb: 2 }}>
            Tell us what you would like help with and the team will get in touch.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Subject"
              value={supportForm.subject}
              onChange={(e) => setSupportForm((f) => ({ ...f, subject: e.target.value }))}
              disabled={supportSending}
            >
              {subjectRows.map((s) => (
                <MenuItem key={s.subject_id} value={s.subject_name}>
                  {s.subject_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="What would you like help with?"
              placeholder="e.g. long division and fractions"
              value={supportForm.focus}
              onChange={(e) => setSupportForm((f) => ({ ...f, focus: e.target.value }))}
              inputProps={{ maxLength: 2000 }}
              disabled={supportSending}
            />
            <TextField
              fullWidth
              size="small"
              label="Preferred day / time (optional)"
              value={supportForm.preferred_day_time}
              onChange={(e) => setSupportForm((f) => ({ ...f, preferred_day_time: e.target.value }))}
              inputProps={{ maxLength: 120 }}
              disabled={supportSending}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Anything else (optional)"
              value={supportForm.notes}
              onChange={(e) => setSupportForm((f) => ({ ...f, notes: e.target.value }))}
              inputProps={{ maxLength: 1000 }}
              disabled={supportSending}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setActionDialog(null)}
            disabled={supportSending}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitRevisionSupport}
            disabled={supportSending}
            sx={{ textTransform: "none", fontWeight: 700, background: GRADIENT }}
          >
            {supportSending ? "Sending…" : "Send request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileStudentDashboard;
